import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#variantMediaMap = this.#readVariantMediaMap();
    this.#scheduleVariantMediaPreload();
    this.#selectInitialFeaturedMedia();
  }

  #controller = new AbortController();
  #variantMediaMap = {};
  #preloadedVariantImages = [];
  #variantMediaPreloaded = false;

  #readVariantMediaMap() {
    const source = this.querySelector('[data-variant-media-map]');
    if (!(source instanceof HTMLScriptElement)) return {};

    try {
      const parsed = JSON.parse(source.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  #scheduleVariantMediaPreload() {
    if (this.dataset.variantRefresh === 'true') return;

    const preload = () => {
      setTimeout(() => this.#preloadVariantMedia(), 0);
    };

    if (document.readyState === 'complete') {
      preload();
    } else {
      window.addEventListener('load', preload, { once: true, signal: this.#controller.signal });
    }
  }

  #preloadVariantMedia() {
    if (this.#variantMediaPreloaded) return;
    this.#variantMediaPreloaded = true;

    Object.values(this.#variantMediaMap)
      .filter((entry) => entry?.url)
      .slice(0, 3)
      .forEach((entry) => {
        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.src = entry.url;
        this.#preloadedVariantImages.push(image);
      });
  }

  #selectVariantMediaImmediately(variantId) {
    const mediaId = this.#variantMediaMap?.[String(variantId)]?.mediaId;
    if (!mediaId) return;

    const variantMedia = Array.from(this.querySelectorAll('[data-media-id]')).find(
      (media) => media instanceof HTMLElement && media.dataset.mediaId === String(mediaId)
    );
    const variantSlide = variantMedia?.closest('slideshow-slide');
    const slideshow = this.slideshow;
    if (!variantSlide || !slideshow) return;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    const variantIndex = slides.indexOf(variantSlide);
    if (variantIndex < 0) return;

    slideshow.select(variantIndex, undefined, { animate: false });
  }

  /**
   * Selects the product-level featured media on the first page load.
   */
  #selectInitialFeaturedMedia() {
    if (this.dataset.variantRefresh === 'true') return;

    const featuredMediaId = this.dataset.productFeaturedMediaId;
    if (!featuredMediaId) return;

    const featuredMedia = Array.from(this.querySelectorAll('[data-media-id]')).find(
      (media) => media instanceof HTMLElement && media.dataset.mediaId === featuredMediaId
    );
    const featuredSlide = featuredMedia?.closest('slideshow-slide');
    if (!featuredSlide) return;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const featuredIndex = slides.indexOf(featuredSlide);
    if (featuredIndex <= 0) return;

    this.slideshow?.select(featuredIndex, undefined, { animate: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const variantId = event.detail?.detail?.variantId ?? event.detail?.variantId;
    this.#selectVariantMediaImmediately(variantId);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const slideshow = newMediaGallery.querySelector('slideshow-component[ref="slideshow"]');
        const selectedVariantMediaId = newMediaGallery.dataset.selectedVariantMediaId;
        let selectedVariantIndex = -1;

        if (slideshow && selectedVariantMediaId) {
          const variantMedia = Array.from(slideshow.querySelectorAll('[data-media-id]')).find(
            (media) => media instanceof HTMLElement && media.dataset.mediaId === selectedVariantMediaId
          );
          const variantSlide = variantMedia?.closest('slideshow-slide');
          const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
          selectedVariantIndex = variantSlide ? slides.indexOf(variantSlide) : -1;
        }

        newMediaGallery.dataset.variantRefresh = 'true';
        this.replaceWith(newMediaGallery);

        if (slideshow && selectedVariantIndex >= 0) {
          let selectionAttempts = 0;
          const selectVariantMedia = () => {
            selectionAttempts += 1;
            const targetControl = slideshow.querySelectorAll('[ref="thumbnails[]"]')[selectedVariantIndex];
            if (targetControl instanceof HTMLElement) targetControl.click();

            const targetSlide = slideshow.querySelectorAll('slideshow-slide')[selectedVariantIndex];
            if (targetSlide?.getAttribute('aria-hidden') !== 'false' && selectionAttempts < 20) {
              setTimeout(selectVariantMedia, 50);
            }
          };

          setTimeout(selectVariantMedia, 0);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
